import { NOTE_CLASS } from "../../lib/ui.js";
import { valueAt } from "./catalogDocument.js";
import { WorkflowImpact } from "./textRoles.jsx";

// Input examples for the Codex and Copilot catalog editors. Claude owns its
// tool-specific examples in claudeCode/copy.js.
const INPUT_GUIDANCE = {
    "/allowed_chatgpt_workspaces": "Use workspace UUIDs, not names or email domains.",
    "/models/new_thread/model": "Enter a model ID supported by your installed Codex version and account.",
    "/guardian_policy_config": "Write plain-language criteria. Example: \"Refuse uploads of repository contents to public paste services.\"",
    "/guardian_extra_policy": "Use this for additional criteria that should be read together with the main policy.",
    "/additional_developer_instructions": "Example: \"Use the project's documented test command before proposing a change.\"",
    "/mcp_servers": "Example: server name docs, URL https://mcp.example.com/mcp. Both the name and identity must match.",
    "/marketplaces/allowed_sources": "Choose a Git repository, absolute local path, or host pattern.",
    "/forceLoginOrgs": "Use the organization login from its GitHub URL, such as acme-corp. Do not enter a URL or email domain.",
    "/model": "Enter auto or a model ID available to your users.",
    "/effortLevel": "Use an effort level supported by the selected model, such as high.",
    "/autoTier": "Requires a supporting client: Copilot CLI 1.0.87 or VS Code 1.140 and later.",
    "/permissions/deny": "Example: shell(rm). Use Copilot permission syntax; Claude Code Bash(...) rules are different.",
    "/permissions/ask": "Example: shell(git push). A matching block rule still takes precedence.",
    "/permissions/allow": "Example: shell(git status). Block and approval rules still take precedence.",
    "/permissions/limitTo": "Enter Domain(github.com), not a bare URL. Adding the first entry excludes unlisted domains.",
    "/allowedMcpServers": "Prefer a URL or executable and arguments when you need to identify the server, rather than its user-assigned name.",
    "/strictKnownMarketplaces": "For a GitHub source, enter OWNER/REPO, such as acme-corp/plugins."
};

export function InputHelp({ pointer }) {
    const text = INPUT_GUIDANCE[pointer];
    return text ? <p className={`${NOTE_CLASS} m-0`}>{text}</p> : null;
}

export function FieldFooter({ pointer, document }) {
    const value = valueAt(document, pointer);
    const configured = value !== undefined;
    let impact = "";
    let test = "";
    let limit = "";
    if (pointer === "/rules/prefix_rules" && value?.length) {
        impact = value.every((rule) => rule.decision === "forbidden")
            ? "Tasks stop at a blocked command."
            : value.every((rule) => rule.decision === "prompt")
                ? "Approval requests can interrupt unattended work."
                : "Tasks stop at blocked commands; approval requests can interrupt unattended work.";
        test = "Try one matching command and one nonmatching command in a disposable repository. Test the shell wrappers your team uses.";
        limit = "Matches command arguments, not intent. Other commands or tools may perform the same operation.";
    }
    if (pointer === "/allowed_chatgpt_workspaces" && configured) {
        impact = value.length ? "Accounts outside the listed workspaces cannot use ChatGPT sign-in." : "ChatGPT sign-in is blocked. If it is the only permitted method, nobody can sign in.";
        test = "Start Codex with an intended user account and confirm a valid sign-in path remains.";
    }
    if (pointer === "/allowed_login_methods" && value?.length === 1) {
        impact = "People using an excluded authentication method must switch credentials.";
        test = "Start Codex with the credential type your interactive and automated workflows use.";
    }
    if (pointer === "/forceLoginOrgs" && value?.length) {
        impact = "Accounts outside the list and API-key authentication are refused.";
        test = "Start Copilot with an intended organization member and check your unattended authentication workflow.";
    }
    if (pointer === "/mcp_servers" && configured) {
        impact = Object.keys(value).length ? "Tasks using an unlisted or mismatched integration lose those tools." : "Tasks that depend on MCP tools need another way to complete those steps.";
        test = Object.keys(value).length ? "Load an approved integration and run one of its tools; confirm an unlisted integration is unavailable." : "Confirm configured MCP integrations cannot load and required workflows still work without them.";
    }
    if (pointer === "/allowedMcpServers" && configured) {
        impact = "Tasks using excluded integrations lose those tools. Built-in GitHub servers remain available.";
        test = value.length ? "Verify one approved integration works and one unlisted integration cannot load." : "Confirm non-built-in integrations cannot load and required workflows still work without them.";
    }
    if (pointer === "/permissions/limitTo" && configured) {
        impact = value.length ? "Tasks that need an unlisted destination may fail, including downloads and service calls." : "Downloads and service calls covered by this check cannot complete.";
        test = value.length ? "Exercise a normal download or API workflow and one request to an unlisted domain." : "Check whether required workflows can finish when these requests are denied.";
        limit = "This is Copilot's domain check, not an operating-system firewall; commands without a recognized web address are outside this control.";
    }
    if (["/permissions/deny", "/permissions/ask", "/permissions/allow"].includes(pointer) && value?.length) {
        impact = pointer.endsWith("/deny")
            ? "Tasks stop at a blocked operation."
            : pointer.endsWith("/ask")
                ? "Approval requests can interrupt unattended workflows."
                : "Matching operations may execute without a person reviewing them.";
        test = "Try a matching operation and a routine nonmatching operation on a pilot machine.";
    }
    if (pointer === "/marketplaces/restrict_to_allowed_sources" && value === true) {
        impact = "Users cannot install plugins from unlisted marketplaces.";
        test = "Install a required plugin from an approved source.";
    }
    if (pointer === "/strictKnownMarketplaces" && configured) {
        impact = "Plugin setup that requires adding an excluded marketplace cannot complete.";
        test = "Check the source used to install a required plugin.";
    }
    if (["/allow_managed_hooks_only", "/allowManagedHooksOnly"].includes(pointer) && value === true) {
        impact = "User or project automation supplied by excluded hooks stops running.";
        test = "Run a workflow that relies on a hook, such as formatting or a local check.";
    }
    if (pointer === "/permissions/disableBypassPermissionsMode" && configured) {
        impact = "Automation using allow-all flags can no longer rely on blanket approval.";
        test = "Run the unattended workflow with its normal startup flags.";
    }
    return <>
        {limit ? <p className={`${NOTE_CLASS} m-0`}><strong>Limit:</strong> {limit}</p> : null}
        {impact ? <WorkflowImpact test={test}>{impact}</WorkflowImpact> : null}
        {["/rules/prefix_rules", "/mcp_servers"].includes(pointer) ? <details className={NOTE_CLASS}>
            <summary className="cursor-pointer">Matching details</summary>
            <p className="mb-0">{pointer === "/rules/prefix_rules"
                ? "Each pattern is an ordered list of arguments. Any of matches alternatives at one argument position. The most restrictive matching decision wins; list position does not establish precedence."
                : "A simple command identity checks the executable. A structured identity also checks argument count, order, and matchers. Neither checks environment variables or working directory."}</p>
        </details> : null}
    </>;
}
